import { DropdownInput } from 'types/mounting'
import { StudioSystemType } from 'types/studio/items'
import { getPanelOrientationsInSystem } from '../railing'

/** Ordered portrait roof hook options (GS-8428). */
export const PORTRAIT_ROOF_HOOK_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Universal Roof Hook Portrait', value: 'ER-I-PRO/01' },
  { label: 'Two-Way Adjustable Universal Roof Hook', value: 'ER-I-PRO/01DA' },
  { label: 'Universal Adjustable Roof Hook', value: 'ER-I-PRO/01A' },
  { label: 'Plain Tile Roof Hook', value: 'ER-I-PRO/02' },
  { label: 'Slate/Prefa Roof Hook', value: 'ER-I-PRO/04' },
  { label: 'Flat tile Tegalit Roof Hook', value: 'ER-I-PRO/07' },
]

/** Tile Slate portrait roof hook options — narrowed subset with renamed /02 label. */
export const TILE_SLATE_PORTRAIT_ROOF_HOOK_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Plain/Slate Tile Roof Hook', value: 'ER-I-PRO/02' },
  { label: 'Slate/Prefa Roof Hook', value: 'ER-I-PRO/04' },
  { label: 'Flat tile Tegalit Roof Hook', value: 'ER-I-PRO/07' },
]

/** Ordered landscape roof hook options (GS-8428). */
export const LANDSCAPE_ROOF_HOOK_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Universal Landscape Adjustable Roof Hook for Tiles', value: 'ER-I-PRO-01A-LS' },
  { label: 'Plain/Slate Tile Roof Hook Landscape', value: 'ER-I-PRO/02A/LS' },
]

export const SOLAR_ROOF_PRO_DEFAULT_PORTRAIT_HOOK = 'ER-I-PRO/01'
export const SOLAR_ROOF_PRO_DEFAULT_LANDSCAPE_HOOK = 'ER-I-PRO-01A-LS'
export const TILE_SLATE_PORTRAIT_DEFAULT_HOOK = 'ER-I-PRO/02'

export const PORTRAIT_ROOF_HOOK_CODES = PORTRAIT_ROOF_HOOK_OPTIONS.map((o) => o.value)
export const TILE_SLATE_PORTRAIT_ROOF_HOOK_CODES = TILE_SLATE_PORTRAIT_ROOF_HOOK_OPTIONS.map((o) => o.value)
export const LANDSCAPE_ROOF_HOOK_CODES = LANDSCAPE_ROOF_HOOK_OPTIONS.map((o) => o.value)

export function getSolarRoofProPortraitHookOptionsForRoofType(
  roofTypeName: string | null | undefined
): ReadonlyArray<{ label: string; value: string }> {
  return roofTypeName === 'Tile Slate' ? TILE_SLATE_PORTRAIT_ROOF_HOOK_OPTIONS : PORTRAIT_ROOF_HOOK_OPTIONS
}

export function getSolarRoofProPortraitHookCodesForRoofType(
  roofTypeName: string | null | undefined
): readonly string[] {
  return getSolarRoofProPortraitHookOptionsForRoofType(roofTypeName).map((o) => o.value)
}

export function getSolarRoofProPortraitHookDefaultForRoofType(roofTypeName: string | null | undefined): string {
  return roofTypeName === 'Tile Slate' ? TILE_SLATE_PORTRAIT_DEFAULT_HOOK : SOLAR_ROOF_PRO_DEFAULT_PORTRAIT_HOOK
}

export function coerceRoofHookCode(
  selected: string | undefined,
  allowedValues: ReadonlyArray<string>,
  fallback: string
): string {
  if (selected && allowedValues.includes(selected)) return selected
  return fallback
}

/** Roof hook dropdown(s) for LHS; mirrors rail-direction portrait/landscape split. */
export function getSolarRoofProRoofHookDropdowns(
  system: StudioSystemType,
  roofTypeName?: string | null
): DropdownInput[] {
  const { portrait, landscape } = getPanelOrientationsInSystem(system)
  const portraitOpts = getSolarRoofProPortraitHookOptionsForRoofType(roofTypeName).map((o) => ({
    label: o.label,
    value: o.value,
  }))
  const portraitDefault = getSolarRoofProPortraitHookDefaultForRoofType(roofTypeName)
  const landscapeOpts = LANDSCAPE_ROOF_HOOK_OPTIONS.map((o) => ({ label: o.label, value: o.value }))

  if (portrait && landscape) {
    return [
      {
        label: 'Portrait Roof Hooks',
        variableName: 'roofHookPortrait',
        default: portraitDefault,
        options: portraitOpts,
      },
      {
        label: 'Landscape Roof Hooks',
        variableName: 'roofHookLandscape',
        default: SOLAR_ROOF_PRO_DEFAULT_LANDSCAPE_HOOK,
        options: landscapeOpts,
      },
    ]
  }
  if (portrait) {
    return [
      {
        label: 'Roof Hook',
        variableName: 'roofHook',
        default: portraitDefault,
        options: portraitOpts,
      },
    ]
  }
  return [
    {
      label: 'Roof Hook',
      variableName: 'roofHook',
      default: SOLAR_ROOF_PRO_DEFAULT_LANDSCAPE_HOOK,
      options: landscapeOpts,
    },
  ]
}
